import {useApolloClient} from '@apollo/client';
import {useInboxBacklogSummaryQuery} from '@transactions/graphql';
import {Inbox, Menu, Plus, Receipt, Wallet} from 'lucide-react';
import * as React from 'react';
import {Link, useLocation} from 'react-router-dom';
import {cn} from 'src/lib/utils';
import {useModals} from 'src/Modals';

const TABS = [
  {
    path: '/',
    label: 'Inbox',
    icon: Inbox,
    // The backlog and the expanded review are the Inbox's own drill-ins.
    matches: (p: string) =>
      ['/', '/backlog', '/inbox/review'].includes(p.replace(/(.)\/+$/, '$1')),
  },
  {
    path: '/ledger',
    label: 'Ledger',
    icon: Receipt,
    // A transaction's or a line's own page is still somewhere in the ledger.
    matches: (p: string) =>
      ['/ledger', '/transactions/', '/raw-transactions/'].some((prefix) =>
        p.startsWith(prefix),
      ),
  },
  {
    path: '/accounts',
    label: 'Accounts',
    icon: Wallet,
    // A balance anchor is opened from an account's reconcile timeline.
    matches: (p: string) =>
      p === '/accounts' || ['/accounts/', '/balance-anchors/'].some((prefix) => p.startsWith(prefix)),
  },
] as const;

/** An account's numbers that a new raw transaction changes. */
const ACCOUNT_DERIVED_FIELDS = [
  'balance',
  'homeBalance',
  'balanceStaleSince',
  'hasRecentActivity',
  'transactionCount',
];

const itemClass = (active: boolean) =>
  cn(
    'flex min-w-0 flex-1 flex-col items-center justify-end gap-0.5 pt-1.5 pb-1 text-[0.7rem] no-underline',
    active ? 'text-foreground' : 'text-muted-foreground',
  );

/**
 * True while a text field has focus — on a phone, while the keyboard is up.
 * The viewport resizes for the keyboard (`interactive-widget=resizes-content`),
 * so a bar pinned to the bottom would ride on top of it.
 *
 * A focused field that is removed (a modal closing on Enter) sends no
 * focusout, so while one has focus, a DOM observer also watches for it
 * leaving the page.
 */
const useTypingFocus = (): boolean => {
  const [typing, setTyping] = React.useState(false);
  React.useEffect(() => {
    const isTextField = (target: EventTarget | null): target is HTMLElement =>
      target instanceof HTMLTextAreaElement ||
      (target instanceof HTMLElement && target.isContentEditable) ||
      (target instanceof HTMLInputElement &&
        !['button', 'checkbox', 'radio', 'range', 'submit', 'reset', 'file', 'color'].includes(
          target.type,
        ));
    let field: HTMLElement | null = null;
    const removal = new MutationObserver(() => {
      if (field != null && !field.isConnected) clear();
    });
    const clear = () => {
      field = null;
      removal.disconnect();
      setTyping(false);
    };
    const onFocusIn = (event: FocusEvent) => {
      if (!isTextField(event.target)) return clear();
      field = event.target;
      removal.observe(document.body, {childList: true, subtree: true});
      setTyping(true);
    };
    const onFocusOut = (event: FocusEvent) => {
      if (!isTextField(event.relatedTarget)) clear();
    };
    // A page that focuses a field as it mounts does so before this effect
    // listens.
    if (isTextField(document.activeElement)) {
      field = document.activeElement;
      removal.observe(document.body, {childList: true, subtree: true});
      setTyping(true);
    }
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      removal.disconnect();
    };
  }, []);
  return typing;
};

/** How far the viewport must shrink below its tallest to count as a keyboard. */
const KEYBOARD_MIN_HEIGHT = 150;

/**
 * The viewport heights seen so far. Kept at module scope and measured from
 * page load on, whether or not the bar is mounted: it isn't on a landscape
 * phone past the mobile breakpoint, and what the keyboard does there has to
 * be known when the phone turns back.
 */
const keyboardHistory = {
  // Keyed by width, so rotating the phone starts a fresh baseline.
  tallest: new Map<number, number>(),
  // A width first seen with the keyboard up (the phone rotated mid-typing)
  // has no unobscured height yet: the keyboard stays open there until the
  // view grows clear of the shortest it has been.
  shortestWhileOpen: new Map<number, number>(),
  open: false,
  listeners: new Set<() => void>(),
};

const measureKeyboard = (viewport: VisualViewport) => {
  const {tallest, shortestWhileOpen} = keyboardHistory;
  const width = Math.round(viewport.width * viewport.scale);
  const height = viewport.height * viewport.scale;
  const known = tallest.get(width);
  if (known != null) {
    const max = Math.max(known, height);
    tallest.set(width, max);
    keyboardHistory.open = max - height > KEYBOARD_MIN_HEIGHT;
  } else if (keyboardHistory.open) {
    const min = Math.min(shortestWhileOpen.get(width) ?? height, height);
    shortestWhileOpen.set(width, min);
    if (height - min > KEYBOARD_MIN_HEIGHT) {
      tallest.set(width, height);
      keyboardHistory.open = false;
    }
  } else {
    tallest.set(width, height);
  }
  for (const listener of keyboardHistory.listeners) listener();
};

const viewport = typeof window === 'undefined' ? undefined : window.visualViewport;
if (viewport != null) {
  measureKeyboard(viewport);
  viewport.addEventListener('resize', () => measureKeyboard(viewport));
}

const subscribeKeyboard = (listener: () => void) => {
  keyboardHistory.listeners.add(listener);
  return () => {
    keyboardHistory.listeners.delete(listener);
  };
};

/**
 * True while the visible viewport is well short of the tallest it has been
 * at this width — on a phone, while the soft keyboard is up. Focus alone
 * can't tell: Android's back gesture closes the keyboard but leaves the
 * field focused, which would leave the tab bar gone with nothing covering
 * its place. The visual viewport shrinks for the keyboard whether the
 * layout resizes (Android) or not (iOS).
 */
const useKeyboardOpen = (): boolean =>
  React.useSyncExternalStore(subscribeKeyboard, () => keyboardHistory.open);

/**
 * Holds the DOM node laid over the tab bar (rendered by BottomBar), for a
 * page to put a bar of its own exactly where the tabs are — same box, same
 * bottom inset — instead of a fixed layer guessing at the tab bar's height.
 * Null while the tab bar isn't shown (on desktop, or while the keyboard is up).
 */
const BottomBarSlotContext = React.createContext<HTMLElement | null>(null);

/** MainPane wraps the routed app in this, passing the mounted slot element. */
export const BottomBarSlotProvider = BottomBarSlotContext.Provider;

export const useBottomBarSlot = () => React.useContext(BottomBarSlotContext);

/**
 * The phone's navigation: the places reached most, in the thumb zone, with
 * Add in the middle for entering cash and other manual rows on the go. More
 * opens the full navigation drawer. Net worth is deliberately not a tab —
 * it lives under More, where it's opened on purpose rather than glanced at.
 */
export const BottomBar = ({
  onMore,
  slotRef,
}: {
  onMore: () => void;
  slotRef: (node: HTMLElement | null) => void;
}) => {
  const {pathname} = useLocation();
  const client = useApolloClient();
  const modals = useModals();
  const typing = useTypingFocus();
  const keyboardOpen = useKeyboardOpen();
  const {data} = useInboxBacklogSummaryQuery();
  const reviewCount = data?.inboxBacklogSummary.recentCount ?? 0;

  if (typing && keyboardOpen) return null;

  const activeTab = TABS.find((tab) => tab.matches(pathname));

  const add = () => {
    modals.createRawTransactionModal.openModal({
      // The new row lands in the review queue. Evicting (not just
      // refetching) the lists reaches pages loaded earlier and no longer on
      // screen too, which a named refetch skips: they reload when next shown.
      onCreate: (transaction) => {
        const {cache} = client;
        cache.evict({id: 'ROOT_QUERY', fieldName: 'allRawTransactions'});
        cache.evict({id: 'ROOT_QUERY', fieldName: 'inboxBacklogSummary'});
        // Its account's derived numbers moved too, and nothing on screen
        // asks for them again unless they're gone.
        const account = cache.identify(transaction.account);
        if (account != null) {
          for (const fieldName of ACCOUNT_DERIVED_FIELDS) cache.evict({id: account, fieldName});
        }
        cache.gc();
      },
    });
  };

  const tab = (index: number) => {
    const {path, label, icon: Icon} = TABS[index];
    const active = activeTab?.path === path;
    return (
      <Link
        to={path}
        aria-current={active ? 'page' : undefined}
        className={itemClass(active)}
      >
        <span className="relative">
          <Icon className="size-5" strokeWidth={active ? 2.25 : 1.75} />
          {path === '/' && reviewCount > 0 && (
            <span className="bg-primary text-primary-foreground absolute -top-1.5 left-3 min-w-4 rounded-full px-1 text-center text-[0.625rem] leading-4 font-medium tabular-nums">
              {reviewCount > 99 ? '99+' : reviewCount}
            </span>
          )}
        </span>
        {label}
      </Link>
    );
  };

  return (
    <nav
      aria-label="Tab bar"
      className="border-border bg-background relative flex shrink-0 items-stretch overflow-hidden border-t pb-[env(safe-area-inset-bottom)]"
    >
      {/* A page's own bar (see BottomBarSlotContext) covers the tabs from
          here; empty, it lets taps through to them. */}
      <div
        ref={slotRef}
        className="pointer-events-none absolute inset-0 z-[1] [&>*]:pointer-events-auto"
      />
      {tab(0)}
      {tab(1)}
      <button type="button" onClick={add} className={itemClass(false)}>
        <span className="bg-primary text-primary-foreground flex size-8 items-center justify-center rounded-full">
          <Plus className="size-5" />
        </span>
        Add
      </button>
      {tab(2)}
      <button
        type="button"
        onClick={onMore}
        // Anywhere the tabs don't cover is somewhere under More.
        className={itemClass(activeTab == null)}
      >
        <Menu className="size-5" strokeWidth={activeTab == null ? 2.25 : 1.75} />
        More
      </button>
    </nav>
  );
};
